import React, { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Autocomplete, Button, TextField, Theme } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getAddressSuggestions } from 'src/services/api/handover';

const useStyles = makeStyles()((theme: Theme) => ({
  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '50vw',
    margin: 'auto',
    gap: '30px',
    justifyContent: 'center',
    width: '100%',
  },
  wrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: '30px',
    padding: '8px 12px',
    backgroundColor: 'unset',
    width: '100%',
    boxShadow: '0px 0px 14px -4px black',
    '& input': {
      backgroundColor: 'unset',
      border: 'none',
      width: '100%',
      marginLeft: '8px',
      marginRight: '8px',
      padding: '4px',
    },
    '& input:focus': {
      outline: 'none',
    },
    '&:hover': {
      boxShadow: '0px 0px 5px -1px black',
      transition: 'box-shadow .2',
    },
  },
  black: {
    color: '#000',
  },
  white: {
    color: '#fff',
  },
}));

const SearchBar = ({
  onSearch,
  placeholder = '',
  color = 'black',
  btnOptions,
  onChange,
  enableAddressAutocomplete = false,
}: {
  onSearch: (search: { [key: string]: string }) => void;
  placeholder?: string;
  color?: 'white' | 'black';
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  btnOptions?: { key: string; label: string }[];
  enableAddressAutocomplete?: boolean;
}) => {
  const { classes } = useStyles();
  const [searchValue, setSearchValue] = useState<string>(
    new URLSearchParams(location.search).get('fullSearch') || '',
  );
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const enterKeyChecker = ({ key }: any) => {
    if (key === 'Enter') {
      onSearch({ address: searchValue });
    }
  };
  const onChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target?.value);
    onChange && onChange(event);
  };

  // Debounced address suggestions; only fetch once the user has typed 2+ chars.
  useEffect(() => {
    if (!enableAddressAutocomplete) return;
    if (searchValue.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      getAddressSuggestions(searchValue.trim())
        .then((results) => {
          if (!cancelled) setSuggestions(results.slice(0, 10));
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchValue, enableAddressAutocomplete]);

  if (enableAddressAutocomplete) {
    return (
      <div className={classes.searchWrapper}>
        <div className={classes.wrapper + ' ' + classes[color]}>
          <SearchIcon onClick={() => onSearch({ address: searchValue })} />
          <Autocomplete
            freeSolo
            fullWidth
            options={suggestions}
            inputValue={searchValue}
            filterOptions={(options) => options}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  borderRadius: '16px',
                  boxShadow: '0px 8px 24px -8px rgba(0, 0, 0, 0.35)',
                  overflow: 'hidden',
                  '& .MuiAutocomplete-listbox': { py: 0.5 },
                  '& .MuiAutocomplete-option': {
                    color: '#000',
                    fontSize: '0.9rem',
                    px: 2,
                    py: 1.25,
                    '&:hover, &[data-focus="true"], &[aria-selected="true"]': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  },
                },
              },
            }}
            onInputChange={(_, value) => setSearchValue(value)}
            onChange={(_, value, reason) => {
              // Both picking a suggestion and pressing Enter on typed text
              // (freeSolo) reach here; run the same search either way.
              if (
                (reason === 'selectOption' || reason === 'createOption') &&
                typeof value === 'string'
              ) {
                setSearchValue(value);
                onSearch({ address: value });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder={placeholder}
                InputProps={{ ...params.InputProps, disableUnderline: true }}
                sx={{ '& input': { color } }}
              />
            )}
          />
        </div>
        {btnOptions &&
          btnOptions.map(({ key, label }) => (
            <Button
              variant="contained"
              key={key}
              onClick={() => onSearch({ key: searchValue })}
            >
              {label}
            </Button>
          ))}
      </div>
    );
  }

  return (
    <div className={classes.searchWrapper}>
      <div className={classes.wrapper + ' ' + classes[color]}>
        <SearchIcon onClick={() => onSearch({ address: searchValue })} />
        <input
          className={classes[color]}
          onChange={onChangeValue}
          onKeyDown={enterKeyChecker}
          value={searchValue}
          placeholder={placeholder}
        />
      </div>
      {btnOptions &&
        btnOptions.map(({ key, label }) => (
          <Button
            variant="contained"
            key={key}
            onClick={() => onSearch({ key: searchValue })}
          >
            {label}
          </Button>
        ))}
    </div>
  );
};
export default SearchBar;
